import {FC, useCallback, useState} from "react";
import Box from "@mui/material/Box";
import {lighten, styled} from "@mui/material/styles";
import apiClient from "../lib/api";
import UploadButton from "@memaro/components/UploadButton.tsx";
import SizeObservableBox from "@memaro/components/SizeObservableBox.tsx";
import Section, {SectionProps} from "../base/Section.tsx";
import VideoPlayer from "./Video/VideoPlayer.tsx";
import LoadingOverlay from "@memaro/components/LoadingOverlay.tsx";
import ButtonOverlay from "@memaro/components/ButtonOverlay.tsx";
import {useFeedback} from "@memaro/feedback";
import FileUploadIcon from "@mui/icons-material/FileUpload";

export type VideoSectionProps = Omit<SectionProps, 'padding'> & {
    profileID: string
    video?: string
    onChange: (sectionID: string, video: string) => void
}

type ContainerProps = {
    height: number
}

const Container = styled(SizeObservableBox)<ContainerProps>(({height}) => ({
    position: "relative",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
    height,
    overflow: 'hidden',

    ':hover .overlay': {
        opacity: 0.9,
    }
}))

const Placeholder = styled(Box)(({theme}) => ({
    display: "flex",
    backgroundColor: lighten(theme.palette.primary.main, 0.4),
    height: "100%",
    width: "100%",
}))

const VideoSection: FC<VideoSectionProps> = ({profileID, sectionID, video, onChange, editMode, ...sectionProps}) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [containerWidth, setContainerWidth] = useState(0);
    const {error} = useFeedback();

    const handleFileSelected = useCallback((file: File) => {
        setLoading(true)
        apiClient.upload(profileID, "video", file)
            .then((m) => onChange(sectionID, m))
            .catch(() => error?.("Er is een fout opgetreden bij het uploaden van het video-bestand"))
            .finally(() => setLoading(false))
    }, [profileID, onChange, sectionID, error])

    return (
        <Section {...sectionProps} editMode={editMode} sectionID={sectionID} padding={0}>
            <Container onSizeChange={setContainerWidth} height={containerWidth * 9 / 16}>
                {!video ? (
                    <Placeholder/>
                ) : (
                    <VideoPlayer src={video}/>
                )}
                {editMode ? (
                    <ButtonOverlay opacity={!video || loading ? 0.9 : 0}>
                        <UploadButton onFileSelected={handleFileSelected} disabled={loading} accept="video/*">
                            <FileUploadIcon />
                            <span>Video uploaden</span>
                        </UploadButton>
                    </ButtonOverlay>
                ) : null}
                <LoadingOverlay opacity={loading ? 1 : 0}/>
            </Container>
        </Section>
    )
}

export default VideoSection
